import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TdsAlert } from '@trv-tds/react';
import { SearchForm, SearchResults } from '../../components/Search';
import { PROPERTY_SEARCH_CONFIGS } from '../../config/providerSearchConfig';
import type { RootState } from '../../redux/store';
import { setSearchCategory } from '../../redux/slices/providerSearchSlice';
import { buildPropertyVendorSpecialtyMap, getCustomerNetworkCodes, getProviderStatusOptions, getStateOptions, getPropertyVendorSpecialtyOptions } from '../../config/updateProviderConfig';
import { FIELDNAME, ERROR_MESSAGES } from '../../constants/textConstants';

interface PropertySearchProps {
  mode: 'external';
}

const PropertySearch: React.FC<PropertySearchProps> = ({ mode }) => {
  const dispatch = useDispatch();
  const externalSearchType = useSelector((state: RootState) => state.providerSearch.externalSearchType);


  // State for API-loaded options
  const [specialtyMap, setSpecialtyMap] = useState<Record<string, Array<{ id: string; value: string }>>>({});
  const [networkOptions, setNetworkOptions] = useState<Array<{ id: string; value: string }>>([]);
  const [approvalStatusOptions, setApprovalStatusOptions] = useState<Array<{ id: string; value: string }>>([]);
  const [stateOptions, setStateOptions] = useState<Array<{ id: string; value: string }>>([]);
  const [vendorSpecialtyOptions, setVendorSpecialtyOptions] = useState<Array<{ id: string; value: string }>>([]);
  const [loadError, setLoadError] = useState<boolean>(false);

  // Set search category when component mounts
  useEffect(() => {
    dispatch(setSearchCategory('property'));
  }, [dispatch]);


  // Load vendor type options and specialty map on mount
  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [specialtyMapData, networkCodes, statusOptions, states, specialtyOptions] = await Promise.all([
          buildPropertyVendorSpecialtyMap(),
          getCustomerNetworkCodes(),
          getProviderStatusOptions(),
          getStateOptions(),
          getPropertyVendorSpecialtyOptions()
        ]);

        setSpecialtyMap(specialtyMapData);
        setNetworkOptions(networkCodes);
        setStateOptions(states);
        setApprovalStatusOptions(statusOptions);
        setVendorSpecialtyOptions(specialtyOptions);
        setLoadError(false);
      } catch (error) {
        console.error('[PropertySearch] Error loading options:', error);
        setLoadError(true);
      }
    };
    loadOptions();
  }, []);

  // Get the appropriate form configuration based on user role & type
  const formConfig = PROPERTY_SEARCH_CONFIGS.external?.[externalSearchType];

  // Clone formConfig and update vendor type options
  // Specialty options will be managed by SearchForm based on selected vendor type

  let updatedFormConfig = undefined;
  if (formConfig) {
    updatedFormConfig = {
      ...formConfig,
      fields: formConfig.fields.map((field: any) => {
        if (field.name === FIELDNAME.VENDOR_SPECIALITY) {
          return { ...field, options: vendorSpecialtyOptions };
        }

        if (field.name === FIELDNAME.NETWORK_SELECTION) {
          return { ...field, options: networkOptions };
        }
        if (field.name === FIELDNAME.APPROVAL_STATUS) {
          return { ...field, options: approvalStatusOptions };
        }
        if (field.name === FIELDNAME.STATE) {
          return { ...field, options: stateOptions };
        }
        return field;
      })
    };
  }


  return (
    <div className="tds-container">
      {loadError ? (
        <div className="tds-container tds-padding-top-xl tds-padding-left-xl">
          <div className="tds-row">
            <div className="tds-col-11">
              <TdsAlert
                heading="Error Alert"
                type="error"
              >
                {ERROR_MESSAGES.REFERENCE_CODE_ERROR}
              </TdsAlert>
            </div>
          </div>
        </div>
      ) : (
        <>
          {updatedFormConfig && (
            <SearchForm
              currentSearchCategory="property"
              formConfig={updatedFormConfig}
              title="Property Search"
              description="Search for properties and related services in your area."
              isExternalUser={true}
              propertySpecialtyMap={specialtyMap}
            />
          )}
          <SearchResults mode={mode} />
        </>
      )}
    </div>
  );
};

export default PropertySearch;